import posthog from 'posthog-js';

/**
 * PostHog configuration options
 */
export interface PostHogConfig {
  apiKey: string;
  host?: string;
  autocapture?: boolean;
  capture_pageview?: boolean;
  persistence?: 'localStorage' | 'cookie' | 'memory' | 'localStorage+cookie';
  loaded?: (posthog: any) => void;
}

/**
 * Initialize PostHog for analytics
 */
export function initPostHog(config?: Partial<PostHogConfig>) {
  const apiKey = config?.apiKey || import.meta.env.VITE_POSTHOG_API_KEY;
  const host = config?.host || import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

  if (!apiKey) {
    console.warn('PostHog API key not provided. Analytics disabled.');
    return;
  }

  posthog.init(apiKey, {
    api_host: host,
    autocapture: config?.autocapture ?? true,
    capture_pageview: config?.capture_pageview ?? true,
    persistence: config?.persistence ?? 'localStorage',
    disable_session_recording: import.meta.env.DEV, // Disable recording in dev
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        creditCard: true,
      },
    },
    loaded: (posthog) => {
      if (import.meta.env.DEV) {
        posthog.debug();
      }
      config?.loaded?.(posthog);
    },
  });
}

/**
 * Identify a user with PostHog
 */
export function identifyPostHogUser(userId: string, properties?: {
  email?: string;
  username?: string;
  plan?: string;
  credits?: number;
  [key: string]: any;
}) {
  posthog.identify(userId, properties);
}

/**
 * Reset user state (on logout)
 */
export function resetPostHog() {
  posthog.reset();
}

/**
 * Track a custom event
 */
export function trackPostHogEvent(
  eventName: string,
  properties?: Record<string, any>
) {
  posthog.capture(eventName, properties);
}

/**
 * Track a page view
 */
export function trackPostHogPageView(path?: string) {
  posthog.capture('$pageview', { $current_url: path || window.location.href });
}

/**
 * Set person properties
 */
export function setPostHogPersonProperties(properties: Record<string, any>) {
  posthog.people.set(properties);
}

/**
 * Set group properties
 */
export function setPostHogGroupProperties(
  groupType: string,
  groupKey: string,
  properties: Record<string, any>
) {
  posthog.group(groupType, groupKey, properties);
}

/**
 * Opt out of tracking
 */
export function optOutOfPostHog() {
  posthog.opt_out_capturing();
}

/**
 * Opt in to tracking
 */
export function optInToPostHog() {
  posthog.opt_in_capturing();
}

/**
 * Check if user has opted out
 */
export function hasOptedOutOfPostHog(): boolean {
  return posthog.has_opted_out_capturing();
}

/**
 * Feature flags helper
 */
export function isPostHogFeatureEnabled(flag: string): boolean {
  return posthog.isFeatureEnabled(flag) ?? false;
}

/**
 * Get all feature flags
 */
export function getAllPostHogFlags(): Record<string, boolean> {
  return posthog.getFeatureFlags() || {};
}

/**
 * Reload feature flags
 */
export function reloadPostHogFlags() {
  posthog.reloadFeatureFlags();
}

/**
 * Toolbar for debugging (only in development)
 */
export function loadPostHogToolbar() {
  if (import.meta.env.DEV) {
    posthog.debug();
    posthog.startSessionRecording();
  }
}
